import { IHttpResponse, IHttpService, IPromise, IHttpPromise } from 'angular';
import * as _ from 'underscore';

import { ListResult } from '../../../ts/fetchers/ListResult';
import { CollectionMapper } from '../../../ts/mappers/CollectionMapper';
import { BaseParamsBuilderResourceQuery } from '../../../ts/resources/ResourceQuery';
import { Search, SearchResult } from '../../../ts/resources/Search';
import {
  FilterOps,
  ILeafFilterClause,
  IStringValuedFieldFilterClause,
  IStringValuedFilterClause,
  FilterFieldNames
} from '../../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ServiceLocator } from '../../ServiceLocator';
import {
  Collection,
  CollectionItem,
  AddRemoveCollectionItem,
  CollectionErroredItem,
  AddRemoveCollectionItemsData,
  DeleteCollectionData,
  CollectionError,
  CollectionVisibility,
  CollectionContentItem
} from './CollectionModels';
import { ICollectionInfo } from './ICollectionInfo';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { ServerService } from '../../../ts/services/ServerService';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { ViewedObjectType, MarkViewedRequest } from '../../../ts/ServerApi/requests/MarkViewedRequest';
import { containsFilterClause, removeFilterClauseFromFilters } from '../../actions/spaces/filters/FilterHelpers';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { Content } from '../../../ts/resources/Content';
import { ServerFilterClauses } from '../../../ts/helpers/ServerFilterClauses';
import { Favorites } from '../../../ts/resources/Favorites';
import { IFavorite } from '../../../ts/ServerApi/types/IFavorite';
import { IGetCollectionResult } from './IGetCollectionResult';

const COLLECTIONS_SERVICE_PATH = 'collections';
export const COLLECTIONS_PATH = `${COLLECTIONS_SERVICE_PATH}/collections`;
export const ITEMS_PATH = `${COLLECTIONS_SERVICE_PATH}/items`;

export interface CollectionsListResponse<T> {
  readonly items?: T[];
  readonly totalCount?: number;
  readonly errors: CollectionError[];
}

export interface GetCollectionResponse {
  readonly collection: Collection;
  readonly errors: CollectionError[];
}

export interface GetCollectionsResponse extends CollectionsListResponse<Collection> {
}

export interface GetCollectionItemsResponse extends CollectionsListResponse<CollectionItem> {
}

export interface AddRemoveItemsResponse {
  readonly response: number;
  readonly errors: CollectionError[];
}

export interface UpdateCollectionData {
  readonly luid: string;
  readonly name?: string;
  readonly description?: string;
  readonly visibility?: CollectionVisibility;
  readonly ownerLuid?: string;
}

export interface DeleteResponse {
  readonly response: string[];
  readonly errors: CollectionError[];
}

const FilterOpsToString = (op: string): string => {
  if (op === FilterOps.eq) {
    return ':';
  } else if (op === FilterOps.gte) {
    return '>=';
  } else if (op === FilterOps.lte) {
    return '<=';
  }
};

export class CollectionsResourceQuery extends BaseParamsBuilderResourceQuery<ICollectionInfo> {
  public getSlice(start: number, size: number): ng.IPromise<ListResult<ICollectionInfo>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);

    // If favorites is being powered by collections, and we have a search and favorites filter
    // we can't go to CS because it doesnt support search, and we cant go to CRASS because it doesnt support favorites
    // So exploreFavoriteContentForUser it is
    if (FeatureFlags.useCollectionsFavorites && matchesFilter && containsFilterClause(filters, FilterFieldNames.isFavorite)) {
      const filtersUpdated = removeFilterClauseFromFilters(filters, FilterFieldNames.isFavorite);
      filtersUpdated.push(ServerFilterClauses.ContentType(['collection']));
      const resource = ServiceLocator.get<Content>('Content');
      return resource.forFavoritesFromCRASS(filtersUpdated).withOrder(this.getOrder())
        .getSlice(start, size) as ng.IPromise<ListResult<ICollectionInfo>>;
    }

    let filterClause = '';
    if (filters.length > 0) {
      filterClause =
        _.without(filters, matchesFilter)
          .map((clause) => {
            const valueClause = clause as IStringValuedFieldFilterClause;
            return valueClause.field + FilterOpsToString(valueClause.operator) + '"' + valueClause.value + '"';
          })
          .join(' AND ');
    }

    if (matchesFilter) {
      const contentTypeClause = 'type:collection';
      if (filterClause.length > 0) {
        filterClause = ' AND ' + filterClause;
      }
      let search = new Search()
        .withOrder(this.getOrder())
        .page(start)
        .limit(size)
        .search((matchesFilter as IStringValuedFilterClause).value)
        .filter(contentTypeClause + filterClause);
      return search.getResults().then((searchResult: SearchResult) => {
        return {
          items: searchResult.items as any[],
          total: searchResult.total,
          index: searchResult.pageIndex
        };
      });
    }

    let orderClause = '';
    const order: IOrderClause[] = this.getOrder();
    if (order.length > 0) {
      orderClause = 'order=' +
        order
          .map((clause) => clause.field + ' ' + (clause.ascending ? 'asc' : 'desc'))
          .join(',');
    }

    let pagingClause = '';
    if (start != null) {
      pagingClause += '&startIndex=' + start;
    }
    if (size != null) {
      pagingClause += '&maxItems=' + size;
    }

    if (filterClause.length > 0) {
      filterClause = '&filter=' + filterClause;
    }

    let spacer: string = '';
    let userAndSiteId: string = '';

    let query = '';
    if (orderClause || filterClause || pagingClause || userAndSiteId) {
      query = (userAndSiteId || '?') + spacer + orderClause + filterClause + pagingClause;
    }

    const url = COLLECTIONS_PATH + query;

    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig)
      .then((response: IHttpResponse<GetCollectionsResponse>) => {
        return CollectionMapper.mapList(response, start);
      })
      .catch(() => {
        return ListResult.empty<ICollectionInfo>();
      });
  }

  public create(name: string): ng.IPromise<ICollectionInfo> {
    const http = ServiceLocator.get<IHttpService>('$http');
    const requestConfig = collectionsRequestConfig('POST', COLLECTIONS_PATH, { name, desc: '' });
    return http(requestConfig).then(CollectionMapper.mapsSingle);
  }

  public delete(luids: string[]): ng.IPromise<DeleteCollectionData> {
    const http = ServiceLocator.get<IHttpService>('$http');
    const luidsParam = luids.join(',');

    const url = COLLECTIONS_PATH + '?luids=' + luidsParam;
    const requestConfig = collectionsRequestConfig('DELETE', url);

    return http(requestConfig).then((response: IHttpResponse<DeleteResponse>) => {
      return CollectionMapper.mapDeleteCollectionsResponse(response);
    });
  }

  public update(updateData: UpdateCollectionData[]): ng.IPromise<{}> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_PATH + '/batchUpdate';
    const requestConfig = collectionsRequestConfig('POST', url, updateData);

    return http(requestConfig).then(() => { return {}; });
  }

  public getByLuid(luid: string): ng.IPromise<IGetCollectionResult> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_PATH + '/' + luid;
    const requestConfig = collectionsRequestConfig('GET', url);

    return http(requestConfig)
      .then(CollectionMapper.mapGetCollectionResult);
  }

  public getCount(): ng.IPromise<number> {
    return this.getSlice(0, 0).then((slice: ListResult<ICollectionInfo>) => slice.total);
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

export class CollectionItemsResourceQuery extends BaseParamsBuilderResourceQuery<CollectionContentItem> {

  public constructor(private collectionLuid: string) {
    super();
  }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<CollectionContentItem>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const favorites = ServiceLocator.get<Favorites>('Favorites');
    const favoritesPromise = favorites.all().getSlice(0, 1000);

    // TODO: As filters and sorts are added, the url will need a ? if there is not siteAndUser. See GetCollections.
    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items';
    const requestConfig = collectionsRequestConfig('GET', url);
    const itemsPromise: IHttpPromise<GetCollectionItemsResponse> = http(requestConfig);

    return Promise.all([itemsPromise, favoritesPromise]).then((result: [IHttpResponse<GetCollectionItemsResponse>, ListResult<IFavorite>]) => {
      const favorites: _.Dictionary<IFavorite> = _.indexBy(result[1].items, 'objectLuid');
      return CollectionMapper.mapItemsList(result[0], start, size, favorites);
    })
    .catch(() => {
      return ListResult.empty<CollectionContentItem>();
    }) as unknown as IPromise<ListResult<CollectionContentItem>>;
  }

  public getCount(): ng.IPromise<number> {
    throw new Error('CollectionsResourceQuery getCount not implemented');
  }

  public getFirst(): ng.IPromise<CollectionContentItem> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }

  public addItemsToCollection(items: IContentInfo[]): ng.IPromise<AddRemoveCollectionItemsData> {
    const http = ServiceLocator.get<IHttpService>('$http');

    let collectionItems: AddRemoveCollectionItem[] = [];
    for (let item of items) {
      collectionItems.push({
        contentLuid: item.luid,
        contentType: item.contentType,
        contentName: item.name,
      });
    }

    const url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items';
    const requestConfig = collectionsRequestConfig('PUT', url, { items: collectionItems });

    return http(requestConfig).then((response: IHttpResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response, this.collectionLuid);
    });
  }

  public static readonly ContentFiltersPerRemoveCall = 30;
  public removeItemsFromCollection(items: IContentInfo[]): Promise<AddRemoveCollectionItemsData> {
    const contentItemsPromises  = [];
    for (let start = 0; start < items.length; start += CollectionItemsResourceQuery.ContentFiltersPerRemoveCall) {
      const contentItemsToFilter = items.slice(start, start + CollectionItemsResourceQuery.ContentFiltersPerRemoveCall);
      contentItemsPromises.push(this.removeItemsFromCollectionImpl(contentItemsToFilter));
    }

    return Promise.all(contentItemsPromises).then((response) => this.reduceAddRemoveCollectionsItemsData(response));
  }

  private removeItemsFromCollectionImpl(items: IContentInfo[]): ng.IPromise<AddRemoveCollectionItemsData> {
    const http = ServiceLocator.get<IHttpService>('$http');
    let url = COLLECTIONS_PATH + '/' + this.collectionLuid + '/items';

    let itemsToRemove: String[] = [];

    for(let item of items) {
      itemsToRemove.push(item.luid + ' ' + item.contentType);
    }

    url += '?luidsAndContentTypes=' + itemsToRemove.join(',');

    const requestConfig = collectionsRequestConfig('DELETE', url);

    return http(requestConfig).then((response: IHttpResponse<AddRemoveItemsResponse>) => {
      return CollectionMapper.mapAddRemoveItemsResponse(response, this.collectionLuid);
    });
  }

  public reduceAddRemoveCollectionsItemsData(items: AddRemoveCollectionItemsData[]): AddRemoveCollectionItemsData {
    const initial: AddRemoveCollectionItemsData = {numItemsSuccessfullyUpdated: 0, errors: [], collectionLuid: this.collectionLuid};
    return items.reduce((previousValue, currentValue) => {
      return {
        numItemsSuccessfullyUpdated: currentValue.numItemsSuccessfullyUpdated + previousValue.numItemsSuccessfullyUpdated,
        errors: [...currentValue.errors, ...previousValue.errors],
        collectionLuid: this.collectionLuid
      };
    }, initial);
  }
}

export class ItemsResourceQuery extends BaseParamsBuilderResourceQuery<CollectionContentItem> {
  private filterClause: string;

  public setFilterClause(filterClause: string) {
    this.filterClause = filterClause;
  }

  public getCount(): ng.IPromise<number> {
    return this.getSlice(0, 0)
      .then(response => response.total);
  }

  public getFirst(): ng.IPromise<CollectionContentItem> {
    return this.getSlice(0, 1)
      .then(response => response.items ? response.items[0] : null);
  }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<CollectionContentItem>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const queryParams = this.buildPagingParams(start, size)
      .concat([ this.buildFilterParam() ]);

    const url = ITEMS_PATH + this.buildQuery(queryParams);

    const requestConfig = collectionsRequestConfig('GET', url);
    return http(requestConfig).then((response: IHttpResponse<GetCollectionItemsResponse>) => {
      return CollectionMapper.mapItemsList(response, start, size, {});
    });
  }

  private buildFilterParam(): string {
    return this.filterClause ? `filter=${this.filterClause}` : '';
  }

  private buildPagingParams(start: number, size: number): string[] {
    return [
      `startIndex=${start}`,
      `maxItems=${size}`,
    ];
  }

  private buildQuery(params: string[]): string {
    return '?' + params.join('&');
  }
}

function collectionsRequestConfig(method: string, url: string, data?: any): ng.IRequestConfig {
  return {
    method: method,
    url: url,
    data: data,
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'}
  };
}

export class CollectionsUsageStats {
  public static markViewed(id: string): IPromise<IResult> {
    return ServiceLocator.get<ServerService>('ServerService').sendRequest(new MarkViewedRequest({
      objectId: id,
      objectType: ViewedObjectType.Collection
    }));
  }
}
